import React, { useEffect } from 'react';
import { sentenceCase } from 'change-case';
import { Row, Col, Button, Form, Input, Checkbox, Select, Tooltip, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ToastNotification from '../../utils/ToastNotification';
import { Client } from '../../types/Client';

interface ClientListSearchFiltersInterfaceProps {
    wrapperStyleClass?: string;
    clients: Client[];
    initialFilters: any;
    onSetSearchFilters(filters: any, opts?: { isPaging: boolean }): void;
}

const JobListSearchFilters: React.FC<ClientListSearchFiltersInterfaceProps> = (
    props: ClientListSearchFiltersInterfaceProps,
): React.ReactElement => {
    const { wrapperStyleClass, clients, onSetSearchFilters, initialFilters } = props;

    const [form] = Form.useForm();

    const clientList = clients
        .map((client: { id: number; name: string }) => ({
            value: client.id,
            key: client.id,
            text: client.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const hasValidFilter = (filters: { [key: string]: unknown }): boolean => {
        return Object.values(filters).some((filter) => typeof filter !== 'undefined');
    };

    const removeDirtyFilters = (filters: { [key: string]: any }): { [key: string]: any } => {
        const filtersToApply = Object.assign(filters);
        Object.keys(filtersToApply).forEach(
            (key) =>
                (!filtersToApply[key] || (Array.isArray(filtersToApply[key]) && filtersToApply[key].length === 0)) &&
                delete filtersToApply[key],
        );
        return filtersToApply;
    };

    const setSearchFilters = (): void => {
        const filters = removeDirtyFilters(form.getFieldsValue());
        if (hasValidFilter(filters)) {
            onSetSearchFilters({ ...filters });
            return;
        }
        onSetSearchFilters({});
        ToastNotification({
            message: 'No search criteria',
            description: 'Job list will be reloaded.',
        });
    };

    const resetFilters = (): void => {
        form.resetFields();
        onSetSearchFilters({});
    };

    useEffect(() => {
        form.setFieldsValue(initialFilters);
    }, [initialFilters]);

    return (
        <div className={wrapperStyleClass || 'px-4 bg-white'}>
            <Form layout="vertical" form={form} id="filter-form" name="filter-form">
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} title="Client name">
                        <Form.Item label="Client name" id="client_name" name="client_name">
                            <Input
                                placeholder="Client name"
                                id="client_name"
                                name="client_name"
                                title="Client name"
                                autoComplete="off"
                                onPressEnter={setSearchFilters}
                                allowClear
                                autoFocus
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }} title="Stuart Client Id">
                        <Form.Item label="Stuart Client Id" id="stuart_client_id" name="stuart_client_id">
                            <Input
                                placeholder="Stuart Client Id"
                                id="stuart_client_id"
                                name="stuart_client_id"
                                title="Stuart Client Id"
                                autoComplete="off"
                                onPressEnter={setSearchFilters}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} title="Api Client Id">
                        <Form.Item label="Api Client Id" id="api_client_id" name="api_client_id">
                            <Input
                                placeholder="Api client reference"
                                id="api_client_id"
                                name="api_client_id"
                                title="Api client reference"
                                autoComplete="off"
                                onPressEnter={setSearchFilters}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        span={4}
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 4 }}
                        title="Only active clients"
                        className="d-flex"
                    >
                        <Row className="d-flex align-items-center">
                            <Form.Item id="active" name="active" valuePropName="checked" className="m-0">
                                <Checkbox id="active" name="active">
                                    Only active clients
                                </Checkbox>
                            </Form.Item>
                        </Row>
                    </Col>
                    <Col
                        span={4}
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 4 }}
                        className="d-flex flex-row align-items-center justify-content-end"
                    >
                        <Space>
                            <Button
                                id="btn-search"
                                name="btn-search"
                                title="Search"
                                onClick={setSearchFilters}
                                className="ant-btn text-white btn-primary bg-primary"
                            >
                                Search
                            </Button>
                            <Button
                                id="btn-reset-filters"
                                name="btn-reset-filters"
                                title="Reset"
                                onClick={resetFilters}
                                className="ant-btn"
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default JobListSearchFilters;
